import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { MarkEmailRead, CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();

  const initialEmail = (location.state as { email?: string } | null)?.email || user?.email || '';

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/api/v1/auth/verify-email', { email, code });
      setSuccess(true);
      if (isAuthenticated) {
        await refreshUser();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || t('verify_email.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResendMessage('');
    setResendLoading(true);
    try {
      await api.post('/api/v1/auth/send-verification-email', { email });
      setResendMessage(t('verify_email.resend_success'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('verify_email.error'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: { xs: 4, sm: 8 }, mb: 4 }}>
        <Paper
          elevation={isMobile ? 0 : 3}
          sx={{
            p: { xs: 2, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {!success ? (
            <>
              <MarkEmailRead color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography
                component="h1"
                variant={isMobile ? 'h5' : 'h4'}
                gutterBottom
                sx={{ fontWeight: 600, textAlign: 'center' }}
              >
                {t('verify_email.title')}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                {t('verify_email.instructions')}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              {resendMessage && (
                <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                  {resendMessage}
                </Alert>
              )}

              <Box component="form" onSubmit={handleVerify} sx={{ width: '100%' }}>
                <TextField
                  required
                  fullWidth
                  label={t('auth.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  required
                  fullWidth
                  label={t('verify_email.code_label')}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputProps={{ maxLength: 6 }}
                  autoFocus
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  {loading ? t('common.loading') : t('verify_email.verify_button')}
                </Button>

                <Button
                  fullWidth
                  onClick={handleResend}
                  disabled={resendLoading || !email}
                  sx={{ textTransform: 'none', mb: 1 }}
                >
                  {resendLoading ? t('common.loading') : t('verify_email.resend_button')}
                </Button>

                <Button
                  fullWidth
                  component={Link}
                  to="/"
                  sx={{ textTransform: 'none' }}
                >
                  {t('verify_email.skip_for_now')}
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />

              <Typography variant="h6" gutterBottom>
                {t('verify_email.success_title')}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('verify_email.success_message')}
              </Typography>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => navigate('/')}
              >
                {t('verify_email.go_home')}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmail;
